import { useEffect, useMemo } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  TextCheckScreen,
  TextQueriesScreen,
  TextRunScreen,
  TextRunsScreen,
  buildTextCheckVm,
  buildTextQueriesVm,
  buildTextRunsVm,
  buildTextRunVm,
  lastTestingCommand,
  platformTestingProducts,
  platformTestingRuns,
  platformTextAddQuery,
  platformTextCancelRun,
  platformTextCheck,
  platformTextCheckStored,
  platformTextDeleteQuery,
  platformTextGenerate,
  platformTextQueries,
  platformTextRun,
  platformTextRuns,
  platformTextStartRun,
  testingAccessOf,
  type TestingAccess,
  type TextCheckActions,
  type TextQueriesActions,
  type TextRunActions,
  type TextRunsActions,
} from '@krp/platform-ui'

import { useAppSelector } from '../../app/hooks'
import { RUN_POLL_MS } from './timings'
import { useMyAccess } from './useMyAccess'

function useTestingAccess(): TestingAccess {
  return testingAccessOf(useMyAccess())
}

const selectQueries = platformTextQueries.select()
const selectProducts = platformTestingProducts.select()

/** «Поиск по тексту · Запросы» (RP-FB34): генерация, свои запросы, отбор по виду и удаление. */
export function TextQueriesBinding() {
  const access = useTestingAccess()
  const openWindow = useOpenWindow()
  const skip = access !== 'allowed'
  const subscription = platformTextQueries.useQuerySubscription(undefined, { skip })
  platformTestingProducts.useQuerySubscription(undefined, { skip })
  const queries = useAppSelector(selectQueries)
  const products = useAppSelector(selectProducts)

  const [generate, generateResult] = platformTextGenerate.useMutation()
  const [add, addResult] = platformTextAddQuery.useMutation()
  const [remove, removeResult] = platformTextDeleteQuery.useMutation()

  const vm = buildTextQueriesVm({
    queries,
    products,
    generated: generateResult.isSuccess && generateResult.data !== undefined ? generateResult.data : null,
    command: lastTestingCommand([generateResult, addResult, removeResult]),
  })

  const refetch = subscription.refetch
  const actions = useMemo<TextQueriesActions>(
    () => ({
      reload: () => void refetch(),
      generate: () => void generate(),
      add: (draft) =>
        add(draft)
          .unwrap()
          .then(() => true)
          .catch(() => false),
      remove: (queryId) => void remove({ queryId }),
      check: (queryId) => openWindow(PLATFORM_WINDOWS.testingTextQueryCheck, { queryId }),
      openCheck: () => openWindow(PLATFORM_WINDOWS.testingTextCheck),
      openRuns: () => openWindow(PLATFORM_WINDOWS.testingTextRuns),
      openProducts: () => openWindow(PLATFORM_WINDOWS.testingTextProducts),
    }),
    [add, generate, openWindow, refetch, remove],
  )
  return <TextQueriesScreen access={access} vm={vm} actions={actions} />
}

/**
 * Проверка запроса. Открытая для сохранённого запроса — сразу проверяет его на сервере с его товаром: удалённый или заменённый
 * генерацией запрос даёт «Запрос не найден», а не пустую форму.
 */
export function TextCheckBinding() {
  const access = useTestingAccess()
  const window = useWindow()
  const openWindow = useOpenWindow()
  const queryId = window.params.queryId ?? ''
  platformTestingProducts.useQuerySubscription(undefined, { skip: access !== 'allowed' })
  const products = useAppSelector(selectProducts)
  const [check, checkResult] = platformTextCheck.useMutation()
  const [checkStored, storedResult] = platformTextCheckStored.useMutation()
  const [save, saveResult] = platformTextAddQuery.useMutation()

  useEffect(() => {
    if (access === 'allowed' && queryId !== '') void checkStored({ queryId })
  }, [access, checkStored, queryId])

  const stored = storedResult.data ?? null
  const initial = useMemo(() => (stored === null ? null : { query: stored.query, productId: stored.expectedProductId }), [stored])
  const productOptions = useMemo(() => (products.data ?? []).map((product) => ({ id: product.id, name: product.name })), [products.data])
  // Последняя проверка — своя, если человек уже нажал «Проверить»; до того — проверка сохранённого запроса.
  const vm = buildTextCheckVm(checkResult.isUninitialized ? storedResult : checkResult, saveResult)
  const actions = useMemo<TextCheckActions>(
    () => ({
      check: (query, expectedProductId) => void check({ query, expectedProductId }),
      save: (draft) => {
        void save(draft)
          .unwrap()
          .then(() => openWindow(PLATFORM_WINDOWS.testingTextQueries))
          .catch(() => undefined)
      },
      openQueries: () => openWindow(PLATFORM_WINDOWS.testingTextQueries),
    }),
    [check, openWindow, save],
  )
  // Сохранённый запрос приходит позже экрана: ключ пересоздаёт поля, когда он проверен, и не переносит их между окнами.
  return <TextCheckScreen key={stored === null ? 'free' : queryId} access={access} initial={initial} products={productOptions} vm={vm} actions={actions} />
}

const selectRuns = platformTextRuns.select()
const selectPhotoRuns = platformTestingRuns.select()

/** Замеры поиска по тексту: последний балл, история, запуск и отмена. */
export function TextRunsBinding() {
  const access = useTestingAccess()
  const openWindow = useOpenWindow()
  const runs = useAppSelector(selectRuns)
  const active = (runs.data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  const subscription = platformTextRuns.useQuerySubscription(undefined, { skip: access !== 'allowed', pollingInterval: active ? RUN_POLL_MS : 0 })
  // Очередь замеров общая с фото: идущий замер по фото тоже не даёт запустить свой — и опрашивается, пока не кончится.
  const photoRuns = useAppSelector(selectPhotoRuns)
  const photoActive = (photoRuns.data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  platformTestingRuns.useQuerySubscription(undefined, { skip: access !== 'allowed', pollingInterval: photoActive ? RUN_POLL_MS : 0 })
  const [start, startResult] = platformTextStartRun.useMutation()
  const [cancel, cancelResult] = platformTextCancelRun.useMutation()

  const vm = buildTextRunsVm({ runs, otherActive: photoActive, command: lastTestingCommand([startResult, cancelResult]) })
  const refetch = subscription.refetch
  const actions = useMemo<TextRunsActions>(
    () => ({
      reload: () => void refetch(),
      start: () => void start(),
      cancel: (runId) => void cancel({ runId }),
      open: (runId) => openWindow(PLATFORM_WINDOWS.testingTextRun, { runId }),
      openQueries: () => openWindow(PLATFORM_WINDOWS.testingTextQueries),
    }),
    [cancel, openWindow, refetch, start],
  )
  return <TextRunsScreen access={access} vm={vm} actions={actions} />
}

/** Разбор замера; пока он идёт — опрашивается. */
export function TextRunBinding() {
  const access = useTestingAccess()
  const window = useWindow()
  const openWindow = useOpenWindow()
  const runId = window.params.runId ?? ''
  const args = useMemo(() => ({ runId }), [runId])
  const run = useAppSelector(useMemo(() => platformTextRun.select(args), [args]))
  const state = run.data?.run.state
  const active = state === 'queued' || state === 'running'
  const subscription = platformTextRun.useQuerySubscription(args, {
    skip: access !== 'allowed' || runId === '',
    pollingInterval: active ? RUN_POLL_MS : 0,
  })
  const [cancel, cancelResult] = platformTextCancelRun.useMutation()

  const vm = buildTextRunVm({ run, command: lastTestingCommand([cancelResult]) })
  const refetch = subscription.refetch
  const actions = useMemo<TextRunActions>(
    () => ({
      reload: () => void refetch(),
      cancel: () => void cancel({ runId }),
      check: (queryId) => openWindow(PLATFORM_WINDOWS.testingTextQueryCheck, { queryId }),
      openRun: (previousRunId) => openWindow(PLATFORM_WINDOWS.testingTextRun, { runId: previousRunId }),
    }),
    [cancel, openWindow, refetch, runId],
  )
  return <TextRunScreen access={access} vm={vm} actions={actions} />
}
